import React, { useState } from 'react';
import { Calendar, DollarSign, Users, CreditCard, Plus, X } from 'lucide-react';
import ImageUploadV2 from '../common/ImageUploadV2';

const BasicInfoStep = ({ formData, updateFormData, errors = {}, clearError }) => {
    const [amenityInput, setAmenityInput] = useState('');

    const handleAddAmenity = () => {
        if (!amenityInput.trim()) return;
        const current = formData.amenities || [];
        if (!current.includes(amenityInput.trim())) {
            updateFormData({ amenities: [...current, amenityInput.trim()] });
        }
        setAmenityInput('');
    };

    const handleRemoveAmenity = (amenity) => {
        const current = formData.amenities || [];
        updateFormData({ amenities: current.filter(a => a !== amenity) });
    };
    const handleEventTypeChange = (type) => {
        updateFormData({
            eventType: type,
            bookingType: type === 'paid' ? 'single' : undefined,
            tickets: type === 'free' ? undefined : formData.tickets,
            ticketRegions: type === 'free' ? undefined : formData.ticketRegions,
            ticketCategories: type === 'free' ? undefined : formData.ticketCategories,
            participantCategories: type === 'paid' ? undefined : formData.participantCategories,
        });
    };

    return (
        <div className="space-y-6">
            {/* Compact Header */}
            <div>
                <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                <p className="text-sm text-gray-600 mt-1">Essential details about your event</p>
            </div>

            {/* Event Name */}
            <div>
                <label htmlFor="eventName" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="eventName"
                    value={formData.eventName}
                    onChange={(e) => { updateFormData({ eventName: e.target.value }); clearError?.('eventName'); }}
                    placeholder="e.g., Tech Conference 2024"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${errors.eventName ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                />
                {errors.eventName && <p className="text-xs text-red-600 mt-1">{errors.eventName}</p>}
            </div>

            {/* Event Cover Image */}
            <ImageUploadV2
                onUploadSuccess={(url) => updateFormData({ coverImage: url })}
                onUploadError={(error) => console.error('Upload error:', error)}
                uploadEndpoint="/api/v2/events/upload/cover-image"
                currentImageUrl={formData.coverImage}
                label="Event Cover Image (Optional)"
                helpText="Displayed on the event card in the dashboard"
            />

            {/* Event Date Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="eventStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Event Start Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="date"
                            id="eventStartDate"
                            value={formData.eventStartDate || ''}
                            onChange={(e) => { updateFormData({ eventStartDate: e.target.value }); clearError?.('eventStartDate'); }}
                            className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${errors.eventStartDate ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                        />
                    </div>
                    {errors.eventStartDate && <p className="text-xs text-red-600 mt-1">{errors.eventStartDate}</p>}
                </div>
                <div>
                    <label htmlFor="eventEndDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Event End Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="date"
                            id="eventEndDate"
                            value={formData.eventEndDate || ''}
                            onChange={(e) => { updateFormData({ eventEndDate: e.target.value }); clearError?.('eventEndDate'); }}
                            min={formData.eventStartDate || ''}
                            className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${errors.eventEndDate ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                        />
                    </div>
                    {errors.eventEndDate && <p className="text-xs text-red-600 mt-1">{errors.eventEndDate}</p>}
                </div>
            </div>

            {/* Event Type */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                    Event Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Free Event */}
                    <button
                        type="button"
                        onClick={() => handleEventTypeChange('free')}
                        className={`
              p-6 rounded-xl border-2 transition-all duration-200 text-left
              ${formData.eventType === 'free'
                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                            }
            `}
                    >
                        <div className="flex items-start space-x-4">
                            <div
                                className={`
                  p-3 rounded-lg
                  ${formData.eventType === 'free' ? 'bg-blue-100' : 'bg-gray-100'}
                `}
                            >
                                <Users className={`w-6 h-6 ${formData.eventType === 'free' ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 mb-1">Free Event</h3>
                                <p className="text-sm text-gray-600">
                                    No payment required for registration
                                </p>
                            </div>
                            <div
                                className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${formData.eventType === 'free' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                `}
                            >
                                {formData.eventType === 'free' && (
                                    <div className="w-3 h-3 bg-white rounded-full" />
                                )}
                            </div>
                        </div>
                    </button>

                    {/* Paid Event */}
                    <button
                        type="button"
                        onClick={() => handleEventTypeChange('paid')}
                        className={`
              p-6 rounded-xl border-2 transition-all duration-200 text-left
              ${formData.eventType === 'paid'
                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                            }
            `}
                    >
                        <div className="flex items-start space-x-4">
                            <div
                                className={`
                  p-3 rounded-lg
                  ${formData.eventType === 'paid' ? 'bg-blue-100' : 'bg-gray-100'}
                `}
                            >
                                <DollarSign className={`w-6 h-6 ${formData.eventType === 'paid' ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 mb-1">Paid Event</h3>
                                <p className="text-sm text-gray-600">
                                    Charge for tickets or registration
                                </p>
                            </div>
                            <div
                                className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${formData.eventType === 'paid' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                `}
                            >
                                {formData.eventType === 'paid' && (
                                    <div className="w-3 h-3 bg-white rounded-full" />
                                )}
                            </div>
                        </div>
                    </button>
                </div>
            </div>

            {/* Booking Type - Only show if Paid */}
            {formData.eventType === 'paid' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Booking Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Single Booking */}
                        <button
                            type="button"
                            onClick={() => updateFormData({ bookingType: 'single' })}
                            className={`
                p-5 rounded-lg border-2 transition-all duration-200 text-left
                ${formData.bookingType === 'single'
                                    ? 'border-blue-500 bg-white shadow-md'
                                    : 'border-blue-200 bg-white hover:border-blue-300'
                                }
              `}
                        >
                            <div className="flex items-start space-x-3">
                                <CreditCard className={`w-5 h-5 mt-0.5 ${formData.bookingType === 'single' ? 'text-blue-600' : 'text-gray-600'}`} />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">Single Booking</h4>
                                    <p className="text-xs text-gray-600">
                                        Each participant pays separately
                                    </p>
                                </div>
                                <div
                                    className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${formData.bookingType === 'single' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                  `}
                                >
                                    {formData.bookingType === 'single' && (
                                        <div className="w-2.5 h-2.5 bg-white rounded-full" />
                                    )}
                                </div>
                            </div>
                        </button>

                        {/* Multi Booking */}
                        <button
                            type="button"
                            onClick={() => updateFormData({ bookingType: 'multi' })}
                            className={`
                p-5 rounded-lg border-2 transition-all duration-200 text-left
                ${formData.bookingType === 'multi'
                                    ? 'border-blue-500 bg-white shadow-md'
                                    : 'border-blue-200 bg-white hover:border-blue-300'
                                }
              `}
                        >
                            <div className="flex items-start space-x-3">
                                <Users className={`w-5 h-5 mt-0.5 ${formData.bookingType === 'multi' ? 'text-blue-600' : 'text-gray-600'}`} />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">Multi Booking</h4>
                                    <p className="text-xs text-gray-600">
                                        Book multiple participants together
                                    </p>
                                </div>
                                <div
                                    className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${formData.bookingType === 'multi' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}
                  `}
                                >
                                    {formData.bookingType === 'multi' && (
                                        <div className="w-2.5 h-2.5 bg-white rounded-full" />
                                    )}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Currency Selection - Only show if Paid */}
            {formData.eventType === 'paid' && (
                <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                        Currency <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <select
                            id="currency"
                            value={formData.currency || 'INR'}
                            onChange={(e) => updateFormData({ currency: e.target.value })}
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white"
                        >
                            <option value="INR">₹ INR (Indian Rupee)</option>
                            <option value="USD">$ USD (US Dollar)</option>
                            <option value="EUR">€ EUR (Euro)</option>
                            <option value="GBP">£ GBP (British Pound)</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        This currency will be used for all ticket prices
                    </p>
                </div>
            )}

            {/* ID Card Background Upload - S3 */}
            <ImageUploadV2
                onUploadSuccess={(url) => updateFormData({ defaultIdCardBackground: url })}
                onUploadError={(error) => console.error('Upload error:', error)}
                uploadEndpoint="/api/v2/events/upload/event-background"
                currentImageUrl={formData.defaultIdCardBackground}
                label="Default ID Card Background (Optional)"
            />

            {/* Event Address */}
            <div>
                <label htmlFor="eventAddress" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Address / Location
                </label>
                <textarea
                    id="eventAddress"
                    value={formData.eventAddress || ''}
                    onChange={(e) => updateFormData({ eventAddress: e.target.value })}
                    placeholder="Full address of the venue"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                />
            </div>

            {/* Event Amenities */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Amenities
                </label>
                <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-3">
                        Add amenities available at this event (e.g., Wifi, Dinner, Parking).
                    </p>

                    <div className="flex space-x-2 mb-3">
                        <input
                            type="text"
                            value={amenityInput}
                            onChange={(e) => setAmenityInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddAmenity()}
                            placeholder="Type amenity name..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <button
                            type="button"
                            onClick={handleAddAmenity}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                        </button>
                    </div>

                    {formData.amenities && formData.amenities.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {formData.amenities.map(amenity => (
                                <span key={amenity} className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-100">
                                    {amenity}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAmenity(amenity)}
                                        className="ml-2 text-blue-400 hover:text-blue-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400 italic">No amenities added yet.</p>
                    )}
                </div>
            </div>

            {/* Authentication Settings */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-gray-900">Authentication Settings</h3>

                {/* Participant Auth Toggle */}
                <div className="flex items-center justify-between">
                    <div>
                        <label className="text-sm font-medium text-gray-700">Enable Participant Authentication</label>
                        <p className="text-xs text-gray-500">Require participants to verify identity</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => updateFormData({ enableParticipantAuth: !formData.enableParticipantAuth })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.enableParticipantAuth ? 'bg-blue-600' : 'bg-gray-200'}`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.enableParticipantAuth ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {/* Event Username (Read-Only) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Username
                        </label>
                        <input
                            type="text"
                            value={formData.eventUsername || 'Auto-generated on save'}
                            disabled
                            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
                        />
                        <p className="text-xs text-gray-500 mt-1">Unique ID for this event</p>
                    </div>

                    {/* Event Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Password
                        </label>
                        <input
                            type="text"
                            value={formData.eventPassword || '123456'}
                            onChange={(e) => updateFormData({ eventPassword: e.target.value })}
                            placeholder="Default: 123456"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">For accessing event portal (editable)</p>
                    </div>
                </div>
            </div>

            {/* Info Box - More Compact */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex">
                    <svg className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <div className="ml-2">
                        <p className="text-xs text-amber-800">
                            You can modify these settings later, but it may affect existing registrations.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInfoStep;